import React, { FC, useState, useEffect, useRef, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {
  verifyCrewOnboardingToken,
  completeCrewOnboarding,
  saveCrewOnboardingDraft,
  getCrewOnboardingDocumentDraft,
  saveCrewOnboardingDocumentDraft,
  getRanks,
  getCrewOnboardingDraft,
  saveCrewOnboardingAcademic,
  saveCrewOnboardingWatchkeeping,
  saveCrewOnboardingKin,
  saveCrewOnboardingBanking,
  saveCrewOnboardingCourses,
  saveCrewOnboardingSeaService,
  saveCrewOnboardingOther,
} from '../core/_requests'
import {
  Rank, CrewFlagDocument,
  CrewDocumentResponse, CrewDocumentForm,
  AcademicQualificationRequest,
  WatchkeepingCertificateRequest,
  NextOfKinRequest,
  CourseCertificate,
  SeaServiceRequest,
  AdditionalDetailsRequest, Reference,
} from '../core/_models'
import { KTSVG } from '../../../../_metronic/helpers'
import PersonalData from './ApplicationForms/PersonalData'
import AcademicBackground from './ApplicationForms/AcademicBackground'
import CoursesCertificates, { buildCourseCertificateState } from './ApplicationForms/CoursesCertificates'
import Documents from './ApplicationForms/Documents'
import NextOfKin from './ApplicationForms/NextOfKin'
import OtherDetails from './ApplicationForms/OtherDetails'
import SeaService from './ApplicationForms/SeaService'
import WatchkeepingCertificates from './ApplicationForms/WatchkeepingCertificates'
import FlagDocuments from './ApplicationForms/Flag'
import BankingDetails from './ApplicationForms/BankingDetails'
import CrewMatrixProfileModal, { isCrewMatrixRankAllowed } from './CrewMatrixProfileModal'

type PageState = 'loading' | 'invalid' | 'form' | 'success' | 'error'

const TABS = [
  'personal',
  'documents',
  'academic',
  'watchkeeping',
  'kin',
  'banking',
  'courses',
  'seaService',
  'flagDocs',
  'other',
  'crewMatrix',
] as const

type TabKey = typeof TABS[number]

interface TabMeta {
  label: string
  subtitle: string
  description: string
  icon: string
}

const TAB_CONFIG: Record<TabKey, TabMeta> = {
  personal: {
    label: 'Personal Data',
    subtitle: 'Identification & contacts',
    description: 'Provide your personal details, physical attributes, contact information, and residential address.',
    icon: '/media/icons/duotune/communication/com006.svg',
  },
  documents: {
    label: 'Documents',
    subtitle: 'CDC, Passport & Visas',
    description: 'Enter details and upload scans of your National CDC, Passport, US Visa, and national ID cards.',
    icon: '/media/icons/duotune/files/fil003.svg',
  },
  academic: {
    label: 'Academic Background',
    subtitle: 'Education & degrees',
    description: 'List your educational qualifications, institutions attended, grades, and passing dates.',
    icon: '/media/icons/duotune/general/gen029.svg',
  },
  watchkeeping: {
    label: 'Watch Keeping',
    subtitle: 'STCW & COP certificates',
    description: 'Enter your STCW Watchkeeping, Certificate of Competency (COC), and Certificate of Proficiency (COP) records.',
    icon: '/media/icons/duotune/maps/map001.svg',
  },
  kin: {
    label: 'Next of Kin',
    subtitle: 'Emergency contacts',
    description: 'Provide primary next-of-kin beneficiary information and emergency telephone contacts.',
    icon: '/media/icons/duotune/general/gen049.svg',
  },
  banking: {
    label: 'Banking Details',
    subtitle: 'Salary & remittance',
    description: 'Enter your designated bank account, SWIFT, and routing codes for monthly salary disbursements.',
    icon: '/media/icons/duotune/finance/fin002.svg',
  },
  courses: {
    label: 'Courses & Certs',
    subtitle: 'STCW safety courses',
    description: 'Record all mandatory STCW basic safety courses, advanced firefighting, and medical first aid certificates.',
    icon: '/media/icons/duotune/art/art002.svg',
  },
  seaService: {
    label: 'Previous Sea Service',
    subtitle: 'Vessel sailing history',
    description: 'Document your previous voyages, vessel names, engine types, tonnage (GRT/DWT), and rank served.',
    icon: '/media/icons/duotune/maps/map004.svg',
  },
  flagDocs: {
    label: 'Flag Document',
    subtitle: 'Flag endorsements',
    description: 'Provide your Flag State Endorsement (CRA/FSE), seaman book, and flag medical certificates.',
    icon: '/media/icons/duotune/general/gen019.svg',
  },
  other: {
    label: 'Other Details',
    subtitle: 'Declarations & references',
    description: 'Complete mandatory maritime background declarations and provide previous employer references.',
    icon: '/media/icons/duotune/general/gen005.svg',
  },
  crewMatrix: {
    label: 'Crew Matrix',
    subtitle: 'OCIMF experience matrix',
    description: 'Review your calculated sea service experience matrix corresponding to your designated rank.',
    icon: '/media/icons/duotune/graphs/gra001.svg',
  },
}

const certificateDetails = [
  'Deck Watch keeping Regulation II/4',
  'Able Seafarer Deck Regulation II/5 (COP)',
  'Engine Room Watch keeping III/4',
  'Able Seafarer Engine Regulation III/5 (COP)',
  'Electro Technical Rating III/7 (COP)',
  'GMDSS Radio Operator IV/2 (COP)',
]

const bloodGroupOptions = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']

const parseSafeDate = (val: any): Date | null => {
  if (!val) return null
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val
  if (Array.isArray(val)) {
    if (val.length >= 3) {
      const year = val[0]
      const month = val[1] - 1
      const day = val[2]
      const d = new Date(year, month, day)
      return isNaN(d.getTime()) ? null : d
    }
    return null
  }
  if (typeof val === 'object') {
    const year = val.year
    const month = (val.monthValue ?? val.month) ? (val.monthValue ? val.monthValue - 1 : undefined) : undefined
    const day = val.dayOfMonth ?? val.day
    if (year !== undefined && month !== undefined && day !== undefined) {
      const d = new Date(year, month, day)
      return isNaN(d.getTime()) ? null : d
    }
    return null
  }
  const parsed = new Date(val)
  return isNaN(parsed.getTime()) ? null : parsed
}

const CrewOnboardingPage: FC = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''

  const [pageState, setPageState] = useState<PageState>('loading')
  const [errorMessage, setErrorMessage] = useState('')
  const [crewName, setCrewName] = useState('')
  const [crewEmail, setCrewEmail] = useState('')
  const [crewId, setCrewId] = useState<number>(0)
  const [ranks, setRanks] = useState<Rank[]>([])
  const [activeTab, setActiveTab] = useState<TabKey>('personal')
  const [saving, setSaving] = useState(false)
  const [savedTabs, setSavedTabs] = useState<Record<string, boolean>>({})
  const [lastSavedTime, setLastSavedTime] = useState<string | null>(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  // ---- Personal Data state ----
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null)
  const [nationality, setNationality] = useState('')
  const [familyName, setFamilyName] = useState('')
  const [placeOfBirth, setPlaceOfBirth] = useState('')
  const [gender, setGender] = useState('')
  const [maritalStatus, setMaritalStatus] = useState('')
  const [bloodGroup, setBloodGroup] = useState('')
  const [department, setDepartment] = useState('')
  const [contactNumber, setContactNumber] = useState('')
  const [telNo, setTelNo] = useState('')
  const [alternateMobNo, setAlternateMobNo] = useState('')
  const [skypeId, setSkypeId] = useState('')
  const [address, setAddress] = useState('')
  const [heightCms, setHeight] = useState<number>(0)
  const [weightKgs, setWeight] = useState<number>(0)
  const [bmiIndex, setBmiIndex] = useState<number>(0)
  const [shoeSize, setShoeSize] = useState('')
  const [boilerSuitSize, setBoilerSuitSize] = useState('')
  const [dateOfAvailability, setDateOfAvailability] = useState<Date | null>(null)
  const [dateOfJoining, setDateOfJoining] = useState<Date | null>(() => new Date())
  const [presentRankId, setPresentRankId] = useState<number | ''>('')
  const [rankAppliedForId, setRankAppliedForId] = useState<number | ''>('')
  const [employeeId, setEmployeeId] = useState('')
  const [notes, setNotes] = useState('')
  const [willingToAcceptLowerRank, setWillingToAcceptLowerRank] = useState(false)
  const [email, setEmail] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // ---- Documents ----
  const [documentForm, setDocumentForm] = useState<CrewDocumentForm>({})
  const [existingDocument, setExistingDocument] = useState<CrewDocumentResponse | null>(null)

  const handleDocumentFormChange = (field: keyof CrewDocumentForm, value: any) => {
    setDocumentForm((prev) => ({ ...prev, [field]: value }))
  }

  // Load existing document form data when crewId becomes available
  useEffect(() => {
    if (!crewId || !token) return
    ;(async () => {
      try {
        const r = await getCrewOnboardingDocumentDraft(token)
        if (r.success && r.data) {
          setDocumentForm(r.data)
          setExistingDocument(r.data)
        }
      } catch (_) {
        // No existing document data
      }
    })()
  }, [crewId, token])

  // ---- Academic ----
  const [academicBackgrounds, setAcademicBackgrounds] = useState<AcademicQualificationRequest[]>([
    {
      qualification: '',
      institutionName: '',
      boardOrUniversity: '',
      dateOfPassing: '',
      gradeOrPercentage: '',
      englishProficiency: '',
    },
  ])

  // ---- Watchkeeping ----
  const [watchkeepingCertificates, setWatchkeepingCertificates] = useState<WatchkeepingCertificateRequest[]>(
    certificateDetails.map((d) => ({
      certificateDetails: d,
      certificateNo: '',
      dateOfIssue: '',
      placeOfIssue: '',
      validUntil: '',
    }))
  )

  // ---- Next of Kin ----
  const [nextOfKin, setNextOfKin] = useState<NextOfKinRequest>({
    crewId: 0,
    civilStatus: null,
    fullName: '',
    relationship: '',
    address: '',
    pinCode: '',
    phoneStdCode: '',
    phoneNumber: '',
  })

  // ---- Banking ----
  const [bankingDetails, setBankingDetails] = useState<any>({
    crewId: 0,
    nameOfBeneficiary: '',
    accountNumber: '',
    bankName: '',
    branchName: '',
    branchCode: '',
    ifscRtgsType: 'IFSC',
    ifscRtgsCode: '',
    swiftIandi: '',
    bankAddress: '',
    accountCurrency: '',
    otherCurrency: '',
    salaryCurrency: '',
    salaryOtherCurrency: '',
    remarks: '',
    basicWages: null,
    fixedOvertime109Hrs: null,
    leaveWages9DaysPerMonth: null,
    subSmb: null,
    tradeAllowance: null,
    otherAllowance: null,
    ownersLoyaltyBonus: null,
    rejoiningBonus: null,
    overtimeRate: null,
    unionSocialDues: 'As per CBA',
    cashAdvanceFromOffice: null,
    deductionBond: null,
    deductionInternetCard: null,
    otherDeductions: null,
    deductionsTotal: null,
    salary: null,
  })

  // ---- Courses ----
  const [courseCertificates, setCourseCertificates] = useState<CourseCertificate[]>(() =>
    buildCourseCertificateState([])
  )

  // ---- Sea Service ----
  const [seaServices, setSeaServices] = useState<SeaServiceRequest[]>([
    {
      serialNo: 1,
      companyName: '',
      vesselName: '',
      typeOfVesselFlag: '',
      grtdrt: '',
      engineType: '',
      kwtBhp: '',
      rank: '',
      fromDate: '',
      toDate: '',
      totalMonthsDays: '',
      reasonForSignOff: '',
    },
  ])

  // ---- Other Details ----
  const [additionalDetails, setAdditionalDetails] = useState<AdditionalDetailsRequest>({
    crewId: 0,
    criminalCaseInvolved: false,
    criminalCaseDetails: '',
    certificateSuspendedRevoked: false,
    certificateSuspendedRevokedDetails: '',
    hasMedicalConditions: false,
    medicalConditionsDetails: '',
    habitualUseDrugsAlcohol: false,
    habitualUseDetails: '',
    workedWithMultinational: false,
    multinationalNationalities: '',
    referralSource: null,
    referralDetails: '',
    pumpExperienceFramoMonths: null,
    pumpExperienceCopMonths: null,
    drydockingExperience: false,
    drydockingRank: '',
    newConstructionExperience: false,
    newConstructionRank: '',
    incidentInvolvement: false,
    incidentDetails: '',
    courtOfEnquiryInvolvement: false,
    courtOfEnquiryDetails: '',
    references: '[]',
    declarationMedicalExam: false,
    declarationMedicalDecision: false,
    declarationNoCriminal: false,
    declarationDocumentsValid: false,
    declarationNotEmployed: false,
    declarationNoAgents: false,
    availabilityDate: '',
    applicantName: '',
    applicantDate: '',
    hasReferenceData: false,
  })
  const [references, setReferences] = useState<Reference[]>([
    { srNo: 1, companyName: '', pic: '', designation: '', phoneNo: '' },
  ])
  const [existingFlagDocuments, setExistingFlagDocuments] = useState<CrewFlagDocument[] | null>(null)
  const flagDocsRef = useRef<any>(null)

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      setPageState('invalid')
      setErrorMessage('No onboarding token provided.')
      return
    }
    ;(async () => {
      try {
        const r = await verifyCrewOnboardingToken(token)
        if (r.valid) {
          setCrewName(r.crewName || '')
          setCrewEmail(r.crewEmail || '')
          setCrewId(r.crewId || 0)
          setEmail(r.crewEmail || '')
          try {
            setRanks(await getRanks())
          } catch {}

          // Load draft data
          try {
            const d = await getCrewOnboardingDraft(token)
            if (d.success) {
              if (d.personal) {
                setNationality(d.personal.nationality || '')
                setFamilyName(d.personal.familyName || '')
                setPlaceOfBirth(d.personal.placeOfBirth || '')
                setGender(d.personal.gender || '')
                setMaritalStatus(d.personal.maritalStatus || '')
                setBloodGroup(d.personal.bloodGroup || '')
                setDepartment(d.personal.department || '')
                setContactNumber(d.personal.contactNumber || '')
                setTelNo(d.personal.telNo || '')
                setAlternateMobNo(d.personal.alternateMobNo || '')
                setSkypeId(d.personal.skypeId || '')
                setAddress(d.personal.address || '')
                setHeight(d.personal.heightCms || 0)
                setWeight(d.personal.weightKgs || 0)
                setBmiIndex(d.personal.bmiIndex || 0)
                setShoeSize(d.personal.shoeSize || '')
                setBoilerSuitSize(d.personal.boilerSuitSize || '')
                setDateOfBirth(parseSafeDate(d.personal.dateOfBirth))
                setDateOfAvailability(parseSafeDate(d.personal.dateOfAvailability))
                setDateOfJoining(parseSafeDate(d.personal.dateOfJoining))
                setPresentRankId(d.personal.presentRankId || '')
                setRankAppliedForId(d.personal.rankAppliedForId || '')
                setEmployeeId(d.personal.employeeId || '')
                setNotes(d.personal.notes || '')
                setWillingToAcceptLowerRank(d.personal.willingToAcceptLowerRank || false)
              }
              if (d.academic && d.academic.length > 0) {
                setAcademicBackgrounds(
                  d.academic.map((item: any) => ({
                    qualification: item.qualification || '',
                    institutionName: item.institutionName || '',
                    boardOrUniversity: item.boardOrUniversity || '',
                    dateOfPassing: item.dateOfPassing || '',
                    gradeOrPercentage: item.gradeOrPercentage || '',
                    englishProficiency: item.englishProficiency || '',
                  }))
                )
              }
              if (d.watchkeeping && d.watchkeeping.length > 0) {
                setWatchkeepingCertificates(
                  d.watchkeeping.map((item: any) => ({
                    id: item.id,
                    certificateDetails: item.certificateDetails || '',
                    certificateNo: item.certificateNo || '',
                    dateOfIssue: item.dateOfIssue || '',
                    placeOfIssue: item.placeOfIssue || '',
                    validUntil: item.validUntil || '',
                    filePath: item.filePath || '',
                  }))
                )
              }
              if (d.kin && Object.keys(d.kin).length > 0) {
                setNextOfKin({
                  crewId: d.kin.crewId || 0,
                  civilStatus: d.kin.civilStatus || null,
                  fullName: d.kin.fullName || '',
                  relationship: d.kin.relationship || '',
                  address: d.kin.address || '',
                  pinCode: d.kin.pinCode || '',
                  phoneStdCode: d.kin.phoneStdCode || '',
                  phoneNumber: d.kin.phoneNumber || '',
                })
              }
              if (d.banking && Object.keys(d.banking).length > 0) {
                setBankingDetails(d.banking)
              }
              if (d.courses) {
                setCourseCertificates(buildCourseCertificateState(d.courses))
              }
              if (d.seaService && d.seaService.length > 0) {
                setSeaServices(d.seaService)
              }
              if (d.other && Object.keys(d.other).length > 0) {
                setAdditionalDetails(d.other)
              }
              if (d.references && d.references.length > 0) {
                setReferences(d.references)
              }
              if (d.flagDocs && d.flagDocs.length > 0) {
                setExistingFlagDocuments(d.flagDocs)
              }
            }
          } catch (err) {
            console.error('Error fetching draft:', err)
          }

          setPageState('form')
        } else {
          setPageState('invalid')
          setErrorMessage(r.message || 'Invalid or expired link.')
        }
      } catch (e: any) {
        setPageState('error')
        setErrorMessage(e?.message || 'Verification failed. Please try again.')
      }
    })()
  }, [token])

  const reloadCourses = async () => {
    if (!token) return
    try {
      const d = await getCrewOnboardingDraft(token)
      if (d.success && d.courses) {
        setCourseCertificates(buildCourseCertificateState(d.courses))
      }
    } catch (e) {
      console.error('Failed to reload courses draft:', e)
    }
  }

  const reloadWatchkeeping = async () => {
    if (!token) return
    try {
      const d = await getCrewOnboardingDraft(token)
      if (d.success && d.watchkeeping) {
        setWatchkeepingCertificates(
          d.watchkeeping.map((item: any) => ({
            id: item.id,
            certificateDetails: item.certificateDetails || '',
            certificateNo: item.certificateNo || '',
            dateOfIssue: item.dateOfIssue || '',
            placeOfIssue: item.placeOfIssue || '',
            validUntil: item.validUntil || '',
            filePath: item.filePath || '',
          }))
        )
      }
    } catch (e) {
      console.error('Failed to reload watchkeeping draft:', e)
    }
  }

  const fmtDate = (d: Date | null) => {
    if (!d || isNaN(d.getTime())) return undefined
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }

  const recordSavedSuccess = (tabKey: TabKey) => {
    setSavedTabs((s) => ({ ...s, [tabKey]: true }))
    setLastSavedTime(
      new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    )
  }

  const saveBasicFields = async (): Promise<boolean> => {
    setSaving(true)
    try {
      const errors: Record<string, string> = {}
      if (!documentForm.cdcNumber?.trim()) errors.cdcNumber = 'CDC Number is required'
      if (!documentForm.cdcIssueDate?.trim()) errors.cdcIssueDate = 'CDC Date of Issue is required'
      if (!documentForm.cdcPlaceOfIssue?.trim()) errors.cdcPlaceOfIssue = 'CDC Place of Issue is required'
      if (!documentForm.cdcExpiryDate?.trim()) errors.cdcExpiryDate = 'CDC Date of Expiry is required'
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors)
        toast.error('Please fill out all mandatory National CDC fields.')
        setSaving(false)
        return false
      }

      const payload: Record<string, any> = {}
      const d = (d: Date | null) => (d ? fmtDate(d) : undefined)
      payload.dateOfBirth = d(dateOfBirth)
      payload.nationality = nationality
      payload.familyName = familyName
      payload.placeOfBirth = placeOfBirth
      payload.gender = gender
      payload.maritalStatus = maritalStatus
      payload.bloodGroup = bloodGroup
      payload.department = department
      payload.contactNumber = contactNumber
      payload.telNo = telNo
      payload.alternateMobNo = alternateMobNo
      payload.skypeId = skypeId
      payload.address = address
      payload.heightCms = heightCms || undefined
      payload.weightKgs = weightKgs || undefined
      payload.bmiIndex = bmiIndex || undefined
      payload.shoeSize = shoeSize
      payload.boilerSuitSize = boilerSuitSize
      payload.dateOfAvailability = d(dateOfAvailability)
      payload.dateOfJoining = d(dateOfJoining)
      payload.presentRankId = presentRankId || undefined
      payload.rankAppliedForId = rankAppliedForId || undefined
      payload.employeeId = employeeId || undefined
      payload.notes = notes || undefined
      payload.willingToAcceptLowerRank = willingToAcceptLowerRank
      payload.email = email || crewEmail

      if (documentForm.cdcNumber?.trim()) {
        await saveCrewOnboardingDocumentDraft(token, documentForm)
      }
      const r = await saveCrewOnboardingDraft(token, payload)
      if (r.success) {
        recordSavedSuccess('personal')
        recordSavedSuccess('documents')
        toast.success('Personal data & draft saved successfully!')
        return true
      } else {
        toast.error(r.message || 'Draft save failed')
        return false
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Draft save failed'
      if (msg.includes('NTBE') || msg.includes('CDC')) {
        setFieldErrors((prev) => ({ ...prev, cdcNumber: msg }))
      }
      toast.error(msg)
      return false
    } finally {
      setSaving(false)
    }
  }

  const goTo = (tab: TabKey) => {
    setActiveTab(tab)
    window.scrollTo({ top: 120, behavior: 'smooth' })
  }

  // Multi-step calculations
  const matrixRank = ranks.find((r) => Number(r.id) === Number(rankAppliedForId || presentRankId))
  const showCrewMatrix = crewId > 0 && isCrewMatrixRankAllowed(matrixRank?.rank)
  const visibleTabs = useMemo(
    () => TABS.filter((t) => t !== 'crewMatrix' || showCrewMatrix),
    [showCrewMatrix]
  )

  const currentTabIndex = visibleTabs.indexOf(activeTab)
  const prevTab = currentTabIndex > 0 ? visibleTabs[currentTabIndex - 1] : null
  const nextTab = currentTabIndex >= 0 && currentTabIndex < visibleTabs.length - 1 ? visibleTabs[currentTabIndex + 1] : null

  const completedCount = visibleTabs.filter((t) => savedTabs[t]).length
  const progressPercentage = Math.round((completedCount / visibleTabs.length) * 100)

  const rankAppliedName =
    ranks.find((r) => Number(r.id) === Number(rankAppliedForId || presentRankId))?.rank || ''

  // Loading State
  if (pageState === 'loading') {
    return (
      <div
        className='d-flex flex-column align-items-center justify-content-center'
        style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}
      >
        <div className='spinner-border text-primary' style={{ width: '3.5rem', height: '3.5rem' }} role='status' />
        <span className='fw-bold text-gray-800 fs-5 mt-4'>Loading Onboarding Portal...</span>
        <span className='text-muted fs-7 mt-1'>Verifying your secure invitation session</span>
      </div>
    )
  }

  // Invalid / Error State
  if (pageState === 'invalid' || pageState === 'error') {
    return (
      <div
        className='d-flex align-items-center justify-content-center p-5'
        style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)' }}
      >
        <div
          className='card border-0 shadow-lg text-center p-5'
          style={{ maxWidth: '520px', borderRadius: '20px', background: '#ffffff' }}
        >
          <div
            className='mx-auto mb-4 rounded-circle d-flex align-items-center justify-content-center bg-light-danger'
            style={{ width: '80px', height: '80px' }}
          >
            <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-3x svg-icon-danger' />
          </div>
          <h3 className='fw-bolder text-gray-900 mb-2'>Invalid or Expired Link</h3>
          <p className='text-gray-600 fs-6 mb-4 leading-relaxed'>
            {errorMessage || 'This onboarding invitation link has expired or has already been used to complete a profile submission.'}
          </p>
          <div className='p-4 rounded-3 bg-light border text-start mb-4'>
            <div className='fw-bold text-gray-800 fs-7 mb-1'>Need assistance?</div>
            <div className='text-muted fs-8'>
              Please contact your crewing officer or email{' '}
              <a href='mailto:support@shipsedge.ai' className='fw-bold text-primary'>
                support@shipsedge.ai
              </a>{' '}
              to request a new onboarding invitation link.
            </div>
          </div>
          <a href='mailto:support@shipsedge.ai' className='btn btn-primary fw-semibold'>
            Contact Crewing Support
          </a>
        </div>
      </div>
    )
  }

  // Success State
  if (pageState === 'success') {
    return (
      <div
        className='d-flex align-items-center justify-content-center p-5'
        style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' }}
      >
        <div
          className='card border-0 shadow-lg text-center p-5'
          style={{ maxWidth: '580px', borderRadius: '20px', background: '#ffffff' }}
        >
          <div
            className='mx-auto mb-4 rounded-circle d-flex align-items-center justify-content-center bg-light-success'
            style={{ width: '86px', height: '86px' }}
          >
            <KTSVG path='/media/icons/duotune/general/gen043.svg' className='svg-icon-3x svg-icon-success' />
          </div>
          <h2 className='fw-bolder text-gray-900 mb-2'>Onboarding Submitted!</h2>
          <span className='badge badge-light-success fs-7 fw-bold px-3 py-2 mb-3'>
            Profile Data Submitted for Verification
          </span>
          <p className='text-gray-600 fs-6 mb-4 leading-relaxed'>
            Thank you, <strong>{crewName}</strong>. Your profile information, STCW certificates, and documents have been submitted to the fleet crewing department for review.
          </p>
          <div className='text-muted fs-8 mt-2'>
            You may now safely close this browser window. For questions, contact{' '}
            <a href='mailto:support@shipsedge.ai' className='text-primary fw-bold'>
              support@shipsedge.ai
            </a>
          </div>
        </div>
      </div>
    )
  }

  const currentTabMeta = TAB_CONFIG[activeTab]

  // Reusable Bottom Action Bar
  const renderActionBar = (onSave: () => Promise<boolean>) => (
    <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mt-6 pt-4 border-top'>
      <div>
        {prevTab ? (
          <button
            type='button'
            className='btn btn-outline btn-outline-secondary d-flex align-items-center gap-2 fw-semibold px-4'
            onClick={() => goTo(prevTab)}
            disabled={saving}
          >
            <i className='bi bi-arrow-left'></i>
            <span>Back to {TAB_CONFIG[prevTab]?.label || 'Previous'}</span>
          </button>
        ) : (
          <div className='text-muted fs-8'>First Section</div>
        )}
      </div>

      <div className='d-flex align-items-center gap-3'>
        {lastSavedTime && (
          <span className='text-muted fs-8 d-none d-md-inline'>
            <i className='bi bi-check-circle-fill text-success me-1'></i>
            Saved at {lastSavedTime}
          </span>
        )}

        <button
          type='button'
          className='btn btn-light-primary fw-bold px-4 d-flex align-items-center gap-2'
          onClick={async () => {
            await onSave()
          }}
          disabled={saving}
        >
          {saving ? (
            <>
              <span className='spinner-border spinner-border-sm me-1' />
              <span>Saving Draft...</span>
            </>
          ) : (
            <>
              <i className='bi bi-save2'></i>
              <span>Save Draft</span>
            </>
          )}
        </button>

        {nextTab ? (
          <button
            type='button'
            className='btn btn-primary fw-bold px-5 d-flex align-items-center gap-2'
            onClick={async () => {
              const ok = await onSave()
              if (ok) {
                goTo(nextTab)
              }
            }}
            disabled={saving}
          >
            <span>Next: {TAB_CONFIG[nextTab]?.label}</span>
            <i className='bi bi-arrow-right'></i>
          </button>
        ) : (
          <button
            type='button'
            className='btn btn-success fw-bold px-5 d-flex align-items-center gap-2'
            onClick={async () => {
              const ok = await onSave()
              if (ok) {
                setShowConfirmModal(true)
              }
            }}
            disabled={saving}
          >
            <i className='bi bi-check-circle-fill'></i>
            <span>Complete My Profile</span>
          </button>
        )}
      </div>
    </div>
  )

  return (
    <>
      <ToastContainer position='top-right' autoClose={4000} />

      {/* Main Container */}
      <div style={{ minHeight: '100vh', background: '#f4f6f9', display: 'flex', flexDirection: 'column' }}>
        {/* Top Navbar */}
        <header className='bg-white border-bottom shadow-sm sticky-top' style={{ zIndex: 1020 }}>
          <div className='container-xl py-3 px-4 d-flex justify-content-between align-items-center'>
            <div className='d-flex align-items-center gap-3'>
              <img
                src='/media/logos/ShipsEdge_light_mode_logo.svg'
                alt='ShipsEdge'
                style={{ height: '34px', objectFit: 'contain' }}
                onError={(e) => {
                  (e.target as HTMLElement).style.display = 'none'
                }}
              />
              <div className='border-start ps-3 d-none d-sm-block'>
                <span className='fw-bolder text-gray-900 fs-5 d-block line-height-1'>Maritime Crew Portal</span>
                <span className='text-muted fs-8'>Candidate Profile & Certificate Onboarding</span>
              </div>
            </div>

            <div className='d-flex align-items-center gap-3'>
              <div className='d-none d-md-flex align-items-center gap-2 px-3 py-1 rounded-pill bg-light-primary text-primary fs-8 fw-semibold'>
                <KTSVG path='/media/icons/duotune/general/gen048.svg' className='svg-icon-2 svg-icon-primary' />
                <span>24-Hour Secure Session</span>
              </div>
              <a
                href='mailto:support@shipsedge.ai'
                className='btn btn-sm btn-light-primary fw-semibold d-flex align-items-center gap-1'
                title='Need help?'
              >
                <KTSVG path='/media/icons/duotune/communication/com008.svg' className='svg-icon-2' />
                <span className='d-none d-sm-inline'>Help & Support</span>
              </a>
            </div>
          </div>
        </header>

        {/* Content Body */}
        <main className='container-xl py-5 px-3 px-md-4 flex-grow-1'>
          {/* Hero Welcome Card */}
          <div
            className='card border border-gray-200 shadow-sm mb-5 overflow-hidden'
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
            }}
          >
            <div className='card-body p-4 p-md-5'>
              <div className='row align-items-center g-4'>
                <div className='col-lg-8'>
                  <div className='d-flex align-items-center gap-2 flex-wrap mb-2'>
                    <span className='badge badge-light-primary text-primary fs-8 px-3 py-1 rounded-pill fw-bold'>
                      Candidate Onboarding
                    </span>
                    {rankAppliedName && (
                      <span className='badge badge-light-warning text-dark fw-bold fs-8 px-3 py-1 rounded-pill'>
                        Rank: {rankAppliedName}
                      </span>
                    )}
                    <span className='badge badge-light-success text-success fs-8 px-3 py-1 rounded-pill fw-bold'>
                      Auto-Save Active
                    </span>
                  </div>
                  <h1 className='text-gray-900 fw-bolder mb-2' style={{ fontSize: '24px' }}>
                    Welcome, {crewName || 'Seafarer'}
                  </h1>
                  <p className='text-gray-600 fs-7 mb-0' style={{ maxWidth: '640px' }}>
                    Please fill out your personal information, certifications, and past sailing experience. You can save your draft and return anytime before your link expires.
                  </p>
                </div>

                <div className='col-lg-4'>
                  <div className='p-4 rounded-3 bg-light border border-gray-200'>
                    <div className='d-flex justify-content-between align-items-center mb-2'>
                      <span className='text-gray-800 fs-7 fw-bold'>Profile Completion</span>
                      <span className='text-primary fs-5 fw-bolder'>{progressPercentage}%</span>
                    </div>
                    <div className='progress bg-secondary' style={{ height: '8px', borderRadius: '4px' }}>
                      <div
                        className='progress-bar bg-primary'
                        role='progressbar'
                        style={{ width: `${progressPercentage}%`, transition: 'width 0.4s ease' }}
                        aria-valuenow={progressPercentage}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      />
                    </div>
                    <div className='d-flex justify-content-between text-gray-500 fs-8 mt-2'>
                      <span className='fw-semibold'>{completedCount} of {visibleTabs.length} sections saved</span>
                      <span>{visibleTabs.length - completedCount} remaining</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stepper Tabs Bar */}
          <div className='card border-0 shadow-sm mb-5' style={{ borderRadius: '14px' }}>
            <div className='card-body p-2'>
              <div
                className='d-flex gap-2 overflow-auto py-1 px-1'
                style={{
                  scrollBehavior: 'smooth',
                  scrollbarWidth: 'thin',
                }}
              >
                {visibleTabs.map((tab, idx) => {
                  const cfg = TAB_CONFIG[tab]
                  const isActive = activeTab === tab
                  const isSaved = Boolean(savedTabs[tab])
                  return (
                    <button
                      key={tab}
                      type='button'
                      onClick={() => goTo(tab)}
                      className={`btn text-start p-3 d-flex align-items-center gap-3 flex-shrink-0 ${
                        isActive
                          ? 'btn-primary shadow-sm'
                          : isSaved
                          ? 'btn-light-success'
                          : 'btn-light'
                      }`}
                      style={{
                        borderRadius: '10px',
                        minWidth: '190px',
                        border: isActive ? 'none' : '1px solid #e5e7eb',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <div
                        className={`w-35px h-35px rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${
                          isActive
                            ? 'bg-white text-primary'
                            : isSaved
                            ? 'bg-success text-white'
                            : 'bg-white text-gray-700 shadow-xs'
                        }`}
                      >
                        {isSaved && !isActive ? (
                          <span className='fw-bolder fs-6'>✓</span>
                        ) : (
                          <span className='fw-bold fs-7'>{idx + 1}</span>
                        )}
                      </div>
                      <div className='d-flex flex-column text-truncate' style={{ maxWidth: '140px' }}>
                        <span
                          className={`fw-bolder fs-7 text-truncate ${
                            isActive ? 'text-white' : 'text-gray-900'
                          }`}
                        >
                          {cfg?.label || tab}
                        </span>
                        <span
                          className={`fs-9 text-truncate ${
                            isActive ? 'text-white text-opacity-75' : 'text-muted'
                          }`}
                        >
                          {isSaved ? 'Completed' : cfg?.subtitle || `Step ${idx + 1}`}
                        </span>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Form Content Card */}
          <div className='card border-0 shadow-sm mb-5' style={{ borderRadius: '16px' }}>
            {/* Section Header */}
            <div className='card-header border-bottom py-4 px-4 px-md-5 d-flex justify-content-between align-items-center flex-wrap gap-3'>
              <div className='d-flex align-items-center gap-3'>
                <div className='w-40px h-40px rounded-3 bg-light-primary d-flex align-items-center justify-content-center flex-shrink-0'>
                  <KTSVG
                    path={currentTabMeta?.icon || '/media/icons/duotune/general/gen005.svg'}
                    className='svg-icon-1 svg-icon-primary'
                  />
                </div>
                <div>
                  <h4 className='fw-bolder text-gray-900 mb-0'>{currentTabMeta?.label}</h4>
                  <span className='text-muted fs-7'>{currentTabMeta?.description}</span>
                </div>
              </div>

              <div className='d-flex align-items-center gap-2'>
                {savedTabs[activeTab] && (
                  <span className='badge badge-light-success fs-8 fw-bold px-3 py-2 rounded-pill'>
                    ✓ Section Saved
                  </span>
                )}
                <span className='text-muted fs-8 d-none d-md-inline'>
                  Step {currentTabIndex + 1} of {visibleTabs.length}
                </span>
              </div>
            </div>

            {/* Section Form Body */}
            <div className='card-body p-4 p-md-6'>
              {/* ===== 1. PERSONAL DATA ===== */}
              {activeTab === 'personal' && (
                <>
                  {/* Verified Credentials Notice Card */}
                  <div
                    className='p-4 rounded-3 mb-4'
                    style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}
                  >
                    <div className='d-flex align-items-center gap-2 mb-2'>
                      <KTSVG
                        path='/media/icons/duotune/general/gen048.svg'
                        className='svg-icon-2 svg-icon-primary'
                      />
                      <span className='fw-bold text-gray-800 fs-7'>Verified Identity & Invitation Profile</span>
                    </div>
                    <div className='row g-3 fs-7 text-gray-700'>
                      <div className='col-md-6'>
                        <span className='text-muted me-2'>Full Name:</span>
                        <strong className='text-gray-900'>{crewName}</strong>
                      </div>
                      <div className='col-md-6'>
                        <span className='text-muted me-2'>Email Address:</span>
                        <strong className='text-gray-900'>{crewEmail}</strong>
                      </div>
                    </div>
                    <p className='text-muted fs-8 mt-2 mb-0'>
                      🔒 Your registered name and email are pre-verified by your fleet administrator. Complete the remaining profile fields below.
                    </p>
                  </div>

                  <PersonalData
                    presentRankId={presentRankId}
                    setPresentRankId={setPresentRankId}
                    rankAppliedForId={rankAppliedForId}
                    setRankAppliedForId={setRankAppliedForId}
                    employeeId={employeeId}
                    setEmployeeId={setEmployeeId}
                    familyName={familyName}
                    setFamilyName={setFamilyName}
                    telNo={telNo}
                    setTelNo={setTelNo}
                    name={crewName}
                    setName={() => {}}
                    contactNumber={contactNumber}
                    setContactNumber={setContactNumber}
                    email={email || crewEmail}
                    setEmail={setEmail}
                    alternateMobNo={alternateMobNo}
                    setAlternateMobNo={setAlternateMobNo}
                    dateOfBirth={dateOfBirth}
                    setDateOfBirth={setDateOfBirth}
                    skypeId={skypeId}
                    setSkypeId={setSkypeId}
                    nationality={nationality}
                    setNationality={setNationality}
                    placeOfBirth={placeOfBirth}
                    setPlaceOfBirth={setPlaceOfBirth}
                    dateOfAvailability={dateOfAvailability}
                    setDateOfAvailability={setDateOfAvailability}
                    dateOfJoining={dateOfJoining}
                    setDateOfJoining={setDateOfJoining}
                    maritalStatus={maritalStatus}
                    setMaritalStatus={setMaritalStatus}
                    department={department}
                    setDepartment={setDepartment}
                    willingToAcceptLowerRank={willingToAcceptLowerRank}
                    setWillingToAcceptLowerRank={setWillingToAcceptLowerRank}
                    boilerSuitSize={boilerSuitSize}
                    setBoilerSuitSize={setBoilerSuitSize}
                    heightCms={heightCms}
                    setHeight={setHeight}
                    weightKgs={weightKgs}
                    setWeight={setWeight}
                    bmiIndex={bmiIndex}
                    setBmiIndex={setBmiIndex}
                    bloodGroup={bloodGroup}
                    setBloodGroup={setBloodGroup}
                    shoeSize={shoeSize}
                    setShoeSize={setShoeSize}
                    address={address}
                    setAddress={setAddress}
                    notes={notes}
                    setNotes={setNotes}
                    roleId={undefined}
                    roleEntityId={undefined}
                    selectedCompany={{ id: 0, name: '' }}
                    setSelectedCompany={() => {}}
                    selectedCompanyAdmin={{ id: 0, name: '' }}
                    setSelectedCompanyAdmin={() => {}}
                    companies={[]}
                    companyAdmins={[]}
                    companiesbyAdmin={[]}
                    fetchCompaniesByAdmin={() => {}}
                    fieldErrors={fieldErrors}
                    ranks={ranks}
                    bloodGroupOptions={bloodGroupOptions}
                    cdcNumber={documentForm.cdcNumber || ''}
                    setCdcNumber={(v) => handleDocumentFormChange('cdcNumber', v)}
                    cdcIssueDate={documentForm.cdcIssueDate || ''}
                    setCdcIssueDate={(v) => handleDocumentFormChange('cdcIssueDate', v)}
                    cdcPlaceOfIssue={documentForm.cdcPlaceOfIssue || ''}
                    setCdcPlaceOfIssue={(v) => handleDocumentFormChange('cdcPlaceOfIssue', v)}
                    cdcExpiryDate={documentForm.cdcExpiryDate || ''}
                    setCdcExpiryDate={(v) => handleDocumentFormChange('cdcExpiryDate', v)}
                    unionMembershipNo={documentForm.unionMembershipNo || ''}
                    setUnionMembershipNo={(v) => handleDocumentFormChange('unionMembershipNo', v)}
                  />
                  {renderActionBar(saveBasicFields)}
                </>
              )}

              {/* ===== 2. DOCUMENTS ===== */}
              {activeTab === 'documents' && (
                <>
                  <Documents
                    documentForm={documentForm}
                    handleDocumentFormChange={handleDocumentFormChange}
                    fieldErrors={fieldErrors}
                    existingDocument={existingDocument}
                    crewId={crewId}
                  />
                  {renderActionBar(async () => {
                    if (!crewId) {
                      toast.error('No crew ID available')
                      return false
                    }
                    const errors: Record<string, string> = {}
                    if (!documentForm.cdcNumber?.trim()) errors.cdcNumber = 'CDC Number is required'
                    if (!documentForm.cdcIssueDate?.trim()) errors.cdcIssueDate = 'Date of Issue is required'
                    if (!documentForm.cdcPlaceOfIssue?.trim()) errors.cdcPlaceOfIssue = 'Place of Issue is required'
                    if (!documentForm.cdcExpiryDate?.trim()) errors.cdcExpiryDate = 'Date of Expiry is required'
                    if (Object.keys(errors).length > 0) {
                      setFieldErrors(errors)
                      toast.error('Please fill out all mandatory National CDC fields.')
                      return false
                    }
                    try {
                      const r = await saveCrewOnboardingDocumentDraft(token, documentForm)
                      if (r.success) {
                        recordSavedSuccess('documents')
                        if (r.data) {
                          setExistingDocument(r.data)
                          setDocumentForm(r.data)
                        }
                        toast.success('Documents saved successfully!')
                        return true
                      } else {
                        toast.error(r.message || 'Failed to save documents')
                        return false
                      }
                    } catch (e: any) {
                      toast.error(e?.message || 'Failed to save documents')
                      return false
                    }
                  })}
                </>
              )}

              {/* ===== 3. ACADEMIC ===== */}
              {activeTab === 'academic' && (
                <>
                  <AcademicBackground
                    academicBackgrounds={academicBackgrounds}
                    handleAcademicBackgroundChange={(i, f, v) => {
                      setAcademicBackgrounds((prev) => {
                        const n = [...prev]
                        n[i] = { ...n[i], [f]: v }
                        return n
                      })
                    }}
                    handleAddAcademicBackground={() =>
                      setAcademicBackgrounds((prev) => [
                        ...prev,
                        {
                          qualification: '',
                          institutionName: '',
                          boardOrUniversity: '',
                          dateOfPassing: '',
                          gradeOrPercentage: '',
                          englishProficiency: '',
                        },
                      ])
                    }
                    handleRemoveAcademicBackground={(i) =>
                      setAcademicBackgrounds((prev) => prev.filter((_, idx) => idx !== i))
                    }
                    existingAcademicDetails={[]}
                    fieldErrors={fieldErrors}
                  />
                  {renderActionBar(async () => {
                    if (!crewId) {
                      toast.error('No crew ID available')
                      return false
                    }
                    try {
                      const r = await saveCrewOnboardingAcademic(token, academicBackgrounds)
                      if (r.success) {
                        recordSavedSuccess('academic')
                        toast.success('Academic background saved!')
                        return true
                      } else {
                        toast.error(r.message || 'Failed to save academic background')
                        return false
                      }
                    } catch (e: any) {
                      toast.error(e?.message || 'Failed to save academic background')
                      return false
                    }
                  })}
                </>
              )}

              {/* ===== 4. WATCHKEEPING ===== */}
              {activeTab === 'watchkeeping' && (
                <>
                  <WatchkeepingCertificates
                    crewId={crewId}
                    watchkeepingCertificates={watchkeepingCertificates}
                    handleWatchkeepingCertificateChange={(i, f, v) => {
                      setWatchkeepingCertificates((prev) => {
                        const n = [...prev]
                        n[i] = { ...n[i], [f]: v }
                        return n
                      })
                    }}
                    fieldErrors={fieldErrors}
                    existingWatchkeepingCertificates={[]}
                    onRefresh={reloadWatchkeeping}
                  />
                  {renderActionBar(async () => {
                    if (!crewId) {
                      toast.error('No crew ID available')
                      return false
                    }
                    try {
                      const r = await saveCrewOnboardingWatchkeeping(token, watchkeepingCertificates)
                      if (r.success) {
                        recordSavedSuccess('watchkeeping')
                        toast.success('Watchkeeping certificates saved!')
                        return true
                      } else {
                        toast.error(r.message || 'Failed to save watchkeeping certificates')
                        return false
                      }
                    } catch (e: any) {
                      toast.error(e?.message || 'Failed to save watchkeeping certificates')
                      return false
                    }
                  })}
                </>
              )}

              {/* ===== 5. NEXT OF KIN ===== */}
              {activeTab === 'kin' && (
                <>
                  {(() => {
                    const handleSaveKin = async (): Promise<boolean> => {
                      if (!crewId) {
                        toast.error('No crew ID available')
                        return false
                      }
                      const errs: Record<string, string> = {}
                      if (!nextOfKin.civilStatus) errs.civilStatus = 'Civil status is required'
                      if (!nextOfKin.fullName?.trim()) errs.fullName = 'Full name is required'
                      if (!nextOfKin.relationship?.trim()) errs.relationship = 'Relationship is required'
                      setFieldErrors(errs)
                      if (Object.keys(errs).length > 0) {
                        toast.error('Please select Civil Status and fill in required Next of Kin fields.')
                        return false
                      }
                      try {
                        const r = await saveCrewOnboardingKin(token, nextOfKin)
                        if (r.success) {
                          recordSavedSuccess('kin')
                          toast.success('Next of kin details saved!')
                          return true
                        } else {
                          toast.error(r.message || 'Failed to save next of kin')
                          return false
                        }
                      } catch (e: any) {
                        toast.error(e?.message || 'Failed to save next of kin')
                        return false
                      }
                    }
                    return (
                      <>
                        <NextOfKin
                          nextOfKin={nextOfKin}
                          onChange={(field, value) => {
                            setNextOfKin((prev) => ({ ...prev, [field]: value }))
                            if (fieldErrors[field]) setFieldErrors((prev) => ({ ...prev, [field]: '' }))
                          }}
                          onSubmit={handleSaveKin}
                          errors={fieldErrors}
                        />
                        {renderActionBar(handleSaveKin)}
                      </>
                    )
                  })()}
                </>
              )}

              {/* ===== 6. BANKING ===== */}
              {activeTab === 'banking' && (
                <>
                  {(() => {
                    const handleSaveBanking = async (): Promise<boolean> => {
                      if (!crewId) {
                        toast.error('No crew ID available')
                        return false
                      }
                      const errs: Record<string, string> = {}
                      if (!bankingDetails.nameOfBeneficiary?.trim()) errs.nameOfBeneficiary = 'Beneficiary Name is required'
                      if (!bankingDetails.accountNumber?.trim()) errs.accountNumber = 'Account Number is required'
                      if (!bankingDetails.bankName?.trim()) errs.bankName = 'Bank Name is required'
                      if (!bankingDetails.accountCurrency) errs.accountCurrency = 'Account Currency is required'
                      setFieldErrors(errs)
                      if (Object.keys(errs).length > 0) {
                        toast.error('Please fill in required Banking Details (Beneficiary Name, Account Number, Bank Name, Currency).')
                        return false
                      }
                      try {
                        const sanitizedBanking = {
                          ...bankingDetails,
                          accountCurrency: bankingDetails.accountCurrency || null,
                          ifscRtgsType: bankingDetails.ifscRtgsType || null,
                        }
                        const r = await saveCrewOnboardingBanking(token, sanitizedBanking)
                        if (r.success) {
                          recordSavedSuccess('banking')
                          toast.success('Banking details saved!')
                          return true
                        } else {
                          toast.error(r.message || 'Failed to save banking details')
                          return false
                        }
                      } catch (e: any) {
                        toast.error(e?.message || 'Failed to save banking details')
                        return false
                      }
                    }
                    return (
                      <>
                        <BankingDetails
                          data={bankingDetails}
                          onChange={(updated) => {
                            setBankingDetails(updated)
                            setFieldErrors({})
                          }}
                          fieldErrors={fieldErrors}
                          onFieldError={() => {}}
                        />
                        {renderActionBar(handleSaveBanking)}
                      </>
                    )
                  })()}
                </>
              )}

              {/* ===== 7. COURSES ===== */}
              {activeTab === 'courses' && (
                <>
                  <CoursesCertificates
                    crewId={crewId}
                    courseCertificates={courseCertificates}
                    handleCourseCertificateChange={(i, f, v) => {
                      setCourseCertificates((prev) => {
                        const n = [...prev]
                        n[i] = { ...n[i], [f]: v }
                        return n
                      })
                    }}
                    handleAddCourseCertificateRow={() => setCourseCertificates((prev) => [...prev, {} as any])}
                    handleRemoveCourseCertificateRow={(i) =>
                      setCourseCertificates((prev) => prev.filter((_, idx) => idx !== i))
                    }
                    fieldErrors={fieldErrors}
                    existingCourseCertificates={[]}
                    onRefresh={reloadCourses}
                  />
                  {renderActionBar(async () => {
                    if (!crewId) {
                      toast.error('No crew ID available')
                      return false
                    }
                    try {
                      const r = await saveCrewOnboardingCourses(token, courseCertificates)
                      if (r.success) {
                        recordSavedSuccess('courses')
                        toast.success('Courses & certificates saved!')
                        return true
                      } else {
                        toast.error(r.message || 'Failed to save courses & certificates')
                        return false
                      }
                    } catch (e: any) {
                      toast.error(e?.message || 'Failed to save courses & certificates')
                      return false
                    }
                  })}
                </>
              )}

              {/* ===== 8. SEA SERVICE ===== */}
              {activeTab === 'seaService' && (
                <>
                  <SeaService
                    seaServices={seaServices}
                    ranks={ranks}
                    handleSeaServiceChange={(i, f, v) => {
                      setSeaServices((prev) => {
                        const n = [...prev]
                        n[i] = { ...n[i], [f]: v }
                        return n
                      })
                    }}
                    handleAddSeaService={() =>
                      setSeaServices((prev) => [
                        ...prev,
                        {
                          serialNo: prev.length + 1,
                          companyName: '',
                          vesselName: '',
                          typeOfVesselFlag: '',
                          grtdrt: '',
                          engineType: '',
                          kwtBhp: '',
                          rank: '',
                          fromDate: '',
                          toDate: '',
                          totalMonthsDays: '',
                          reasonForSignOff: '',
                        },
                      ])
                    }
                    handleRemoveSeaService={(i) => setSeaServices((prev) => prev.filter((_, idx) => idx !== i))}
                    fieldErrors={fieldErrors}
                  />
                  {renderActionBar(async () => {
                    if (!crewId) {
                      toast.error('No crew ID available')
                      return false
                    }
                    try {
                      const r = await saveCrewOnboardingSeaService(token, seaServices)
                      if (r.success) {
                        recordSavedSuccess('seaService')
                        toast.success('Previous sea service saved!')
                        return true
                      } else {
                        toast.error(r.message || 'Failed to save sea service')
                        return false
                      }
                    } catch (e: any) {
                      toast.error(e?.message || 'Failed to save sea service')
                      return false
                    }
                  })}
                </>
              )}

              {/* ===== 9. FLAG DOCS ===== */}
              {activeTab === 'flagDocs' && (
                <>
                  <FlagDocuments
                    ref={flagDocsRef}
                    crewId={crewId}
                    fieldErrors={fieldErrors}
                    markSaved={() => {
                      recordSavedSuccess('flagDocs')
                    }}
                    setIsDirty={() => {}}
                    existingFlagDocuments={existingFlagDocuments}
                  />
                  {renderActionBar(async () => {
                    if (flagDocsRef.current) {
                      const ok = await flagDocsRef.current.saveAll()
                      if (ok) {
                        recordSavedSuccess('flagDocs')
                        toast.success('Flag documents saved!')
                        return true
                      }
                    }
                    return false
                  })}
                </>
              )}

              {/* ===== 10. OTHER ===== */}
              {activeTab === 'other' && (
                <>
                  {(() => {
                    const handleSaveOther = async (): Promise<boolean> => {
                      if (!crewId) {
                        toast.error('No crew ID available')
                        return false
                      }
                      try {
                        const sanitizedDetails = { ...additionalDetails }
                        delete sanitizedDetails.references
                        if (!sanitizedDetails.referralSource) sanitizedDetails.referralSource = null
                        const r = await saveCrewOnboardingOther(token, sanitizedDetails, references)
                        if (r.success) {
                          recordSavedSuccess('other')
                          toast.success('Other details saved!')
                          return true
                        } else {
                          toast.error(r.message || 'Failed to save other details')
                          return false
                        }
                      } catch (e: any) {
                        toast.error(e?.message || 'Failed to save other details')
                        return false
                      }
                    }
                    return (
                      <>
                        <OtherDetails
                          additionalDetails={additionalDetails}
                          setAdditionalDetails={setAdditionalDetails}
                          references={references}
                          setReferences={setReferences}
                          existingAdditionalDetails={null}
                          companyAwarenessName={crewName}
                          errors={fieldErrors}
                          onSubmit={handleSaveOther}
                        />
                        {renderActionBar(handleSaveOther)}
                      </>
                    )
                  })()}
                </>
              )}

              {/* ===== 11. CREW MATRIX ===== */}
              {activeTab === 'crewMatrix' && (
                <>
                  <CrewMatrixProfileModal
                    inline
                    title='Crew Matrix'
                    crewRecord={
                      {
                        id: crewId,
                        name: crewName,
                        email: crewEmail,
                        rank: ranks.find((r) => Number(r.id) === Number(rankAppliedForId || presentRankId))?.rank || '',
                        department,
                        nationality,
                        status: 'Active' as any,
                        contactNumber,
                        telNo,
                        familyName,
                        dateOfBirth: dateOfBirth?.toISOString() || '',
                        bloodGroup,
                        maritalStatus,
                        heightCms,
                        weightKgs,
                        address,
                      } as any
                    }
                    seaServices={seaServices}
                  />
                  {renderActionBar(async () => {
                    recordSavedSuccess('crewMatrix')
                    toast.info('Crew matrix saved.')
                    return true
                  })}
                </>
              )}
            </div>

            {/* Card Footer Bar */}
            <div className='card-footer py-4 px-4 px-md-5 d-flex justify-content-between align-items-center flex-wrap gap-3 bg-light'>
              <div className='d-flex align-items-center gap-2 text-muted fs-7'>
                <KTSVG path='/media/icons/duotune/general/gen048.svg' className='svg-icon-2 svg-icon-primary' />
                <span>All draft entries are securely saved under your candidate profile.</span>
              </div>
              <button
                className='btn btn-success fw-bold px-5 d-flex align-items-center gap-2'
                onClick={() => setShowConfirmModal(true)}
                disabled={saving}
              >
                <i className='bi bi-check-circle-fill fs-5'></i>
                <span>Complete & Submit Profile</span>
              </button>
            </div>
          </div>
        </main>

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 100000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(4px)',
            }}
          >
            <div
              className='card border-0 shadow-lg'
              style={{
                maxWidth: '480px',
                width: '92%',
                borderRadius: '18px',
                overflow: 'hidden',
              }}
            >
              <div className='card-body p-5 text-center'>
                <div
                  className='mx-auto mb-3 rounded-circle d-flex align-items-center justify-content-center bg-light-primary'
                  style={{ width: '64px', height: '64px' }}
                >
                  <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-2x svg-icon-primary' />
                </div>
                <h4 className='fw-bolder text-gray-900 mb-2'>Submit Candidate Profile?</h4>
                <p className='text-gray-600 fs-7 leading-relaxed mb-4'>
                  Once you confirm and submit, your profile details and documents will be locked and sent to the crewing department for maritime verification.
                </p>

                <div className='p-3 rounded bg-light border text-start mb-4 fs-8'>
                  <div className='d-flex justify-content-between mb-1'>
                    <span className='text-muted'>Candidate Name:</span>
                    <strong className='text-gray-800'>{crewName}</strong>
                  </div>
                  <div className='d-flex justify-content-between mb-1'>
                    <span className='text-muted'>Designated Rank:</span>
                    <strong className='text-gray-800'>{rankAppliedName || 'N/A'}</strong>
                  </div>
                  <div className='d-flex justify-content-between'>
                    <span className='text-muted'>Completed Sections:</span>
                    <strong className='text-success'>{completedCount} of {visibleTabs.length}</strong>
                  </div>
                </div>

                <div className='d-flex gap-3 justify-content-end'>
                  <button
                    type='button'
                    className='btn btn-light fw-semibold flex-fill'
                    onClick={() => setShowConfirmModal(false)}
                    disabled={saving}
                  >
                    Keep Editing
                  </button>
                  <button
                    type='button'
                    className='btn btn-success fw-bold flex-fill d-flex align-items-center justify-content-center gap-2'
                    onClick={async () => {
                      setSaving(true)
                      try {
                        const payload: Record<string, any> = {}
                        const d = (d: Date | null) => (d ? fmtDate(d) : undefined)
                        payload.dateOfBirth = d(dateOfBirth)
                        payload.nationality = nationality
                        payload.familyName = familyName
                        payload.placeOfBirth = placeOfBirth
                        payload.gender = gender
                        payload.maritalStatus = maritalStatus
                        payload.bloodGroup = bloodGroup
                        payload.department = department
                        payload.contactNumber = contactNumber
                        payload.telNo = telNo
                        payload.alternateMobNo = alternateMobNo
                        payload.skypeId = skypeId
                        payload.address = address
                        payload.heightCms = heightCms || undefined
                        payload.weightKgs = weightKgs || undefined
                        payload.bmiIndex = bmiIndex || undefined
                        payload.shoeSize = shoeSize
                        payload.boilerSuitSize = boilerSuitSize
                        payload.dateOfAvailability = d(dateOfAvailability)
                        payload.dateOfJoining = d(dateOfJoining)
                        payload.presentRankId = presentRankId || undefined
                        payload.rankAppliedForId = rankAppliedForId || undefined
                        payload.employeeId = employeeId || undefined
                        payload.notes = notes || undefined
                        payload.willingToAcceptLowerRank = willingToAcceptLowerRank
                        payload.email = email || crewEmail

                        if (documentForm.cdcNumber?.trim()) {
                          await saveCrewOnboardingDocumentDraft(token, documentForm)
                        }
                        const r = await completeCrewOnboarding(token, payload)
                        if (r.success) {
                          setPageState('success')
                        } else {
                          toast.error(r.message || 'Submission failed')
                          setShowConfirmModal(false)
                        }
                      } catch (e: any) {
                        toast.error(e?.message || 'Submission failed')
                        setShowConfirmModal(false)
                      } finally {
                        setSaving(false)
                      }
                    }}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className='spinner-border spinner-border-sm me-1' />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <i className='bi bi-check2-circle'></i>
                        <span>Confirm & Submit</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Global Footer */}
        <footer className='py-4 border-top bg-white mt-auto'>
          <div className='container-xl text-center text-muted fs-8'>
            &copy; {new Date().getFullYear()} <strong>ShipsEdge</strong> Maritime Management Systems. All rights reserved. &bull;{' '}
            <a href='mailto:support@shipsedge.ai' className='text-primary fw-semibold'>
              support@shipsedge.ai
            </a>
          </div>
        </footer>
      </div>
    </>
  )
}

export default CrewOnboardingPage
